import type { FirebaseApp } from "firebase/app";
import type { Auth, Persistence } from "firebase/auth";
import {
  browserLocalPersistence,
  browserPopupRedirectResolver,
  browserSessionPersistence,
  getAuth,
  initializeAuth,
  inMemoryPersistence,
} from "firebase/auth";

/**
 * Persistence order for the admin Firebase Auth instance.
 *
 * @remarks
 * The SDK default (`getAuth`) prefers IndexedDB. That persistence closes its
 * database whenever the tab is hidden (`pagehide` / `visibilitychange`) and
 * then rejects the post-sign-in write with "Database is closing/hidden".
 * Safari hides the opener tab while the Google popup has focus, so staff saw
 * a successful Google login fail on the admin side. localStorage persistence
 * has no visibility lifecycle, so it is preferred here; session and in-memory
 * persistence remain as fallbacks for browsers that block site storage.
 */
export const ADMIN_AUTH_PERSISTENCE: Persistence[] = [
  browserLocalPersistence,
  browserSessionPersistence,
  inMemoryPersistence,
];

const isAlreadyInitializedError = (error: unknown): boolean =>
  typeof error === "object" &&
  error !== null &&
  "code" in error &&
  (error as { code?: unknown }).code === "auth/already-initialized";

/**
 * Creates (or reuses) the admin Firebase Auth instance with
 * {@link ADMIN_AUTH_PERSISTENCE}.
 *
 * Both admin bootstrap paths (`src/lib/firebase.ts` and the shared
 * `HelloCashflowDomain` initializer) must go through this helper so the first
 * caller decides persistence and later callers reuse the same instance.
 */
export const createAdminAuth = (app: FirebaseApp): Auth => {
  try {
    return initializeAuth(app, {
      persistence: ADMIN_AUTH_PERSISTENCE,
      popupRedirectResolver: browserPopupRedirectResolver,
    });
  } catch (error) {
    if (isAlreadyInitializedError(error)) {
      return getAuth(app);
    }
    throw error;
  }
};
